import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getChats,
  getOrganizationChats,
  getUserConnections,
  getUserPendingInvites,
} from "../api/api";
import { presenceStampMs } from "../components/utils";
import { useInboxSocket } from "../hooks/useInboxSocket";
import {
  getTotalUnreadCount,
  shouldIncrementUnread,
} from "../components/messaging_page/chatListUtils";

const EMPTY_CONNECTION_OVERRIDES = {};

/**
 * @param {Array<object>} connections
 * @param {number|string} userId
 * @returns {number}
 */
export function countPendingIncomingConnections(connections, userId) {
  return (connections || []).filter(
    (conn) =>
      conn.status === "pending" &&
      Number(conn.receiver_id) === Number(userId)
  ).length;
}

/**
 * Viewer-relative status from a connection row.
 * @param {object|null|undefined} connection
 * @param {number|string} userId
 * @returns {string}
 */
function viewerConnectionStatus(connection, userId) {
  if (!connection?.status) return "none";
  if (connection.status === "pending") {
    return Number(connection.sender_id) === Number(userId)
      ? "pending_sent"
      : "pending_received";
  }
  return connection.status;
}

/**
 * The other user on a connection relative to the viewer.
 * @param {object|null|undefined} connection
 * @param {number|string} userId
 * @returns {number|null}
 */
function peerUserIdFromConnection(connection, userId) {
  if (!connection) return null;
  const sender = Number(connection.sender_id);
  const receiver = Number(connection.receiver_id);
  const self = Number(userId);
  if (Number.isFinite(sender) && sender === self) return receiver;
  if (Number.isFinite(receiver) && receiver === self) return sender;
  if (Number.isFinite(sender)) return sender;
  if (Number.isFinite(receiver)) return receiver;
  return null;
}

/**
 * Active affiliation org ids from the signed-in user object.
 * @param {object|null|undefined} user
 * @returns {number[]}
 */
function getActiveOrgIds(user) {
  if (!user || user.type === "organization") return [];
  return (user.affiliations || [])
    .filter((a) => a.status === "active" && a.organization?.id != null)
    .map((a) => Number(a.organization.id))
    .filter(Number.isFinite);
}

const UserRealtimeContext = createContext(null);

/**
 * Shell-owned user inbox WebSocket, connection badge, and message unread badges.
 *
 * @param {{
 *   userId: number|string|null|undefined,
 *   user?: object|null,
 *   children: React.ReactNode,
 * }} props
 * @returns {JSX.Element}
 */
export function UserRealtimeProvider({ userId, user = null, children }) {
  const [pendingConnectionCount, setPendingConnectionCount] = useState(0);
  const [pendingAffiliationCount, setPendingAffiliationCount] = useState(0);
  const [messagingUnreadCount, setMessagingUnreadCount] = useState(0);
  const [orgUnreadById, setOrgUnreadById] = useState(
    /** @type {Record<string, number>} */ ({})
  );
  const [connectionOverrides, setConnectionOverrides] = useState(
    /** @type {Record<string, { status: string, connectionId: number }>} */ ({})
  );
  const [presenceByUserId, setPresenceByUserId] = useState(
    /** @type {Record<string, string>} */ ({})
  );

  const subscribersRef = useRef(new Set());
  const pendingIdsRef = useRef(/** @type {Set<number>} */ (new Set()));
  const activeChatRef = useRef(
    /** @type {{ chatId: number|null, orgId: number|null }} */ ({
      chatId: null,
      orgId: null,
    })
  );
  const orgIdsKey = getActiveOrgIds(user).sort((a, b) => a - b).join(",");

  /**
   * Broadcast a local connection change so badges and Connect buttons stay in sync.
   * @param {{
   *   peerUserId?: number|string|null,
   *   status: string,
   *   connectionId?: number|string|null,
   * }} change
   * @returns {void}
   */
  const reportConnectionChange = useCallback((change) => {
    const peerId = Number(change?.peerUserId);
    const connectionId = Number(change?.connectionId);
    const status = change?.status;
    if (!status) return;

    if (Number.isFinite(peerId) && peerId > 0) {
      setConnectionOverrides((prev) => ({
        ...prev,
        [String(peerId)]: {
          status,
          connectionId: Number.isFinite(connectionId)
            ? connectionId
            : prev[String(peerId)]?.connectionId || 0,
        },
      }));
    }

    if (!Number.isFinite(connectionId) || connectionId <= 0) return;

    if (status === "pending_received" || status === "pending") {
      if (!pendingIdsRef.current.has(connectionId)) {
        pendingIdsRef.current.add(connectionId);
        setPendingConnectionCount(pendingIdsRef.current.size);
      }
    } else if (
      status === "active" ||
      status === "accepted" ||
      status === "rejected" ||
      status === "left" ||
      status === "none"
    ) {
      if (pendingIdsRef.current.delete(connectionId)) {
        setPendingConnectionCount(pendingIdsRef.current.size);
      }
    }
  }, []);

  const refreshPendingConnections = useCallback(async () => {
    if (!userId) {
      pendingIdsRef.current = new Set();
      setPendingConnectionCount(0);
      setConnectionOverrides({});
      return;
    }
    try {
      const result = await getUserConnections(userId);
      if (result.success && Array.isArray(result.data)) {
        const pending = result.data.filter(
          (conn) =>
            conn.status === "pending" &&
            Number(conn.receiver_id) === Number(userId)
        );
        pendingIdsRef.current = new Set(
          pending.map((c) => Number(c.id)).filter(Number.isFinite)
        );
        setPendingConnectionCount(pending.length);
      }
    } catch {
      // Keep prior count on transient failures.
    }
  }, [userId]);

  const userEmail = user?.email || "";

  const refreshPendingAffiliations = useCallback(async () => {
    if (!userId || !userEmail) {
      setPendingAffiliationCount(0);
      return;
    }
    try {
      const result = await getUserPendingInvites(userEmail);
      if (result.success && Array.isArray(result.data)) {
        setPendingAffiliationCount(result.data.length);
      }
    } catch {
      // Keep prior count on transient failures.
    }
  }, [userId, userEmail]);

  const refreshUnreadCounts = useCallback(async () => {
    if (!userId) {
      setMessagingUnreadCount(0);
      setOrgUnreadById({});
      return;
    }

    const orgIds = orgIdsKey
      ? orgIdsKey.split(",").map((id) => Number(id)).filter(Number.isFinite)
      : [];

    try {
      const personalRes = await getChats(userId);
      if (personalRes.success && Array.isArray(personalRes.data)) {
        setMessagingUnreadCount(getTotalUnreadCount(personalRes.data));
      }
    } catch {
      // Keep prior personal unread on transient failures.
    }

    if (orgIds.length === 0) {
      setOrgUnreadById({});
      return;
    }

    const next = /** @type {Record<string, number>} */ ({});
    await Promise.all(
      orgIds.map(async (orgId) => {
        try {
          const res = await getOrganizationChats(orgId, userId);
          if (res.success && Array.isArray(res.data)) {
            next[String(orgId)] = getTotalUnreadCount(res.data);
          } else {
            next[String(orgId)] = 0;
          }
        } catch {
          next[String(orgId)] = 0;
        }
      })
    );
    setOrgUnreadById(next);
  }, [userId, orgIdsKey]);

  useEffect(() => {
    refreshPendingConnections();
  }, [refreshPendingConnections]);

  useEffect(() => {
    refreshPendingAffiliations();
  }, [refreshPendingAffiliations]);

  useEffect(() => {
    refreshUnreadCounts();
  }, [refreshUnreadCounts]);

  const notifySubscribers = useCallback((method, payload) => {
    for (const sub of subscribersRef.current) {
      try {
        sub[method]?.(payload);
      } catch (err) {
        console.error(`UserRealtime subscriber ${method} error:`, err);
      }
    }
  }, []);

  /**
   * Pages report which chat is currently open so shell unread does not double-count.
   * @param {{ chatId?: number|string|null, orgId?: number|string|null }} active
   * @returns {void}
   */
  const setActiveInboxChat = useCallback((active) => {
    const chatId =
      active?.chatId != null && Number.isFinite(Number(active.chatId))
        ? Number(active.chatId)
        : null;
    const orgId =
      active?.orgId != null && Number.isFinite(Number(active.orgId))
        ? Number(active.orgId)
        : null;
    activeChatRef.current = { chatId, orgId };
  }, []);

  /**
   * Reduce shell unread after a chat was marked read locally.
   * @param {number} clearedCount
   * @param {number|string|null|undefined} orgId - null/undefined = personal messaging
   * @returns {void}
   */
  const reportUnreadCleared = useCallback((clearedCount, orgId) => {
    const n = Math.max(0, Number(clearedCount) || 0);
    if (n <= 0) return;
    const orgKey =
      orgId != null && Number(orgId) > 0 ? String(Number(orgId)) : null;
    if (orgKey) {
      setOrgUnreadById((prev) => ({
        ...prev,
        [orgKey]: Math.max(0, (Number(prev[orgKey]) || 0) - n),
      }));
    } else {
      setMessagingUnreadCount((prev) => Math.max(0, prev - n));
    }
  }, []);

  const applyInboxUnreadIncrement = useCallback(
    (message) => {
      if (!userId || !message) return;
      const open = activeChatRef.current;
      if (!shouldIncrementUnread(message, userId, open.chatId)) return;

      const orgId = Number(message.org_id) || 0;
      if (orgId > 0) {
        const key = String(orgId);
        setOrgUnreadById((prev) => ({
          ...prev,
          [key]: (Number(prev[key]) || 0) + 1,
        }));
      } else {
        setMessagingUnreadCount((prev) => prev + 1);
      }
    },
    [userId]
  );

  const handleInboxMessage = useCallback(
    (message) => {
      applyInboxUnreadIncrement(message);
      notifySubscribers("onInboxMessage", message);
    },
    [applyInboxUnreadIncrement, notifySubscribers]
  );

  const handleChatRemoved = useCallback(
    (chatId) => {
      notifySubscribers("onChatRemoved", chatId);
      // Heal totals — removed chat may have carried unread.
      refreshUnreadCounts();
    },
    [notifySubscribers, refreshUnreadCounts]
  );

  const handleConnectionNotification = useCallback(
    (connection) => {
      if (!userId || !connection) return;
      const id = Number(connection.id);
      const isReceiver = Number(connection.receiver_id) === Number(userId);
      const peerId = peerUserIdFromConnection(connection, userId);
      const viewerStatus = viewerConnectionStatus(connection, userId);

      if (Number.isFinite(peerId) && peerId > 0) {
        reportConnectionChange({
          peerUserId: peerId,
          status: viewerStatus,
          connectionId: Number.isFinite(id) ? id : 0,
        });
      }

      if (!isReceiver) {
        refreshPendingConnections();
      }
    },
    [userId, refreshPendingConnections, reportConnectionChange]
  );

  /**
   * Record a last_online stamp, keeping the newest value per user.
   * @param {number|string|null|undefined} peerUserId
   * @param {string} lastOnline
   * @returns {void}
   */
  const reportPresence = useCallback((peerUserId, lastOnline) => {
    const id = Number(peerUserId);
    if (!Number.isFinite(id) || id <= 0 || !lastOnline) return;
    const key = String(id);
    setPresenceByUserId((prev) => {
      const current = prev[key] || "";
      if (presenceStampMs(current) >= presenceStampMs(lastOnline)) return prev;
      return { ...prev, [key]: lastOnline };
    });
  }, []);

  /**
   * Seed last_online from chat member payloads returned by REST.
   * @param {Array<object>|null|undefined} chats
   * @returns {void}
   */
  const seedPresenceFromChats = useCallback(
    (chats) => {
      for (const chat of chats || []) {
        for (const member of chat?.other_members || []) {
          if (member?.user_id && member.last_online) {
            reportPresence(member.user_id, member.last_online);
          }
        }
      }
    },
    [reportPresence]
  );

  const handleReconnect = useCallback(() => {
    refreshPendingConnections();
    refreshPendingAffiliations();
    refreshUnreadCounts();
    notifySubscribers("onReconnect");
  }, [refreshPendingConnections, refreshPendingAffiliations, refreshUnreadCounts, notifySubscribers]);

  useInboxSocket(userId || null, {
    onInboxMessage: handleInboxMessage,
    onChatRemoved: handleChatRemoved,
    onConnectionNotification: handleConnectionNotification,
    onReconnect: handleReconnect,
    onPresence: ({ user_id, last_online }) => reportPresence(user_id, last_online),
  }, { enablePresence: user?.type !== "organization" });

  /**
   * Register messaging/org handlers for inbox events.
   * @param {{
   *   onInboxMessage?: (message: object) => void,
   *   onChatRemoved?: (chatId: number) => void,
   *   onReconnect?: () => void,
   * }} handlers
   * @returns {() => void} unsubscribe
   */
  const subscribeInbox = useCallback((handlers) => {
    const entry = {
      onInboxMessage: handlers?.onInboxMessage,
      onChatRemoved: handlers?.onChatRemoved,
      onReconnect: handlers?.onReconnect,
    };
    subscribersRef.current.add(entry);
    return () => {
      subscribersRef.current.delete(entry);
    };
  }, []);

  const value = useMemo(
    () => ({
      pendingConnectionCount,
      pendingAffiliationCount,
      messagingUnreadCount,
      orgUnreadById,
      connectionOverrides,
      refreshPendingConnections,
      refreshPendingAffiliations,
      refreshUnreadCounts,
      setActiveInboxChat,
      reportUnreadCleared,
      reportConnectionChange,
      subscribeInbox,
      presenceByUserId,
      reportPresence,
      seedPresenceFromChats,
    }),
    [
      pendingConnectionCount,
      pendingAffiliationCount,
      messagingUnreadCount,
      orgUnreadById,
      connectionOverrides,
      refreshPendingConnections,
      refreshPendingAffiliations,
      refreshUnreadCounts,
      setActiveInboxChat,
      reportUnreadCleared,
      reportConnectionChange,
      subscribeInbox,
      presenceByUserId,
      reportPresence,
      seedPresenceFromChats,
    ]
  );

  return (
    <UserRealtimeContext.Provider value={value}>
      {children}
    </UserRealtimeContext.Provider>
  );
}

const EMPTY_ORG_UNREAD = {};

/**
 * Access shell realtime inbox + badge counts.
 * Safe outside provider (returns no-op defaults) for logged-out shell.
 * @returns {{
 *   pendingConnectionCount: number,
 *   pendingAffiliationCount: number,
 *   messagingUnreadCount: number,
 *   orgUnreadById: Record<string, number>,
 *   connectionOverrides: Record<string, { status: string, connectionId: number }>,
 *   refreshPendingConnections: () => Promise<void>,
 *   refreshPendingAffiliations: () => Promise<void>,
 *   refreshUnreadCounts: () => Promise<void>,
 *   setActiveInboxChat: (active: object) => void,
 *   reportUnreadCleared: (clearedCount: number, orgId?: number|string|null) => void,
 *   reportConnectionChange: (change: object) => void,
 *   subscribeInbox: (handlers: object) => () => void,
 *   presenceByUserId: Record<string, string>,
 *   reportPresence: (userId: number|string, lastOnline: string) => void,
 *   seedPresenceFromChats: (chats: Array<object>) => void,
 * }}
 */
export function useUserRealtime() {
  const ctx = useContext(UserRealtimeContext);
  return (
    ctx || {
      pendingConnectionCount: 0,
      pendingAffiliationCount: 0,
      messagingUnreadCount: 0,
      orgUnreadById: EMPTY_ORG_UNREAD,
      connectionOverrides: EMPTY_CONNECTION_OVERRIDES,
      refreshPendingConnections: async () => {},
      refreshPendingAffiliations: async () => {},
      refreshUnreadCounts: async () => {},
      setActiveInboxChat: () => {},
      reportUnreadCleared: () => {},
      reportConnectionChange: () => {},
      subscribeInbox: () => () => {},
      presenceByUserId: {},
      reportPresence: () => {},
      seedPresenceFromChats: () => {},
    }
  );
}

/**
 * Connection status for a peer, merged with session-wide overrides from any accept/connect/disconnect.
 * @param {number|string|null|undefined} peerUserId
 * @param {string} [fallbackStatus]
 * @param {number|string} [fallbackConnectionId]
 * @returns {{
 *   status: string,
 *   connectionId: number,
 *   reportConnectionChange: (change: object) => void,
 *   refreshPendingConnections: () => Promise<void>,
 * }}
 */
export function usePeerConnectionStatus(
  peerUserId,
  fallbackStatus = "none",
  fallbackConnectionId = 0
) {
  const {
    connectionOverrides,
    reportConnectionChange,
    refreshPendingConnections,
  } = useUserRealtime();
  const peerId = Number(peerUserId);
  const override =
    Number.isFinite(peerId) && peerId > 0
      ? connectionOverrides[String(peerId)]
      : null;
  return {
    status: override ? override.status || "none" : fallbackStatus || "none",
    connectionId: override
      ? Number(override.connectionId) || 0
      : Number(fallbackConnectionId) || 0,
    reportConnectionChange,
    refreshPendingConnections,
  };
}

/**
 * Newest last_online stamp for a user, overlaying live presence on REST data.
 *
 * @param {number|string|null|undefined} userId
 * @param {string} [fallback]
 * @returns {string}
 */
export function usePresenceLastOnline(userId, fallback = "") {
  const { presenceByUserId } = useUserRealtime();
  const id = Number(userId);
  if (!Number.isFinite(id) || id <= 0) return fallback || "";
  return presenceByUserId[String(id)] || fallback || "";
}

/**
 * Subscribe to inbox events for the lifetime of the calling component.
 * @param {{
 *   onInboxMessage?: (message: object) => void,
 *   onChatRemoved?: (chatId: number) => void,
 *   onReconnect?: () => void,
 * } | null} handlers
 * @param {boolean} [enabled=true]
 * @returns {void}
 */
export function useInboxSubscription(handlers, enabled = true) {
  const { subscribeInbox } = useUserRealtime();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled || !handlers) return undefined;
    return subscribeInbox({
      onInboxMessage: (msg) => handlersRef.current?.onInboxMessage?.(msg),
      onChatRemoved: (id) => handlersRef.current?.onChatRemoved?.(id),
      onReconnect: () => handlersRef.current?.onReconnect?.(),
    });
  }, [subscribeInbox, enabled]);
}

/**
 * Keep shell "active chat" in sync while a messaging surface is mounted.
 * @param {number|string|null|undefined} chatId
 * @param {number|string|null|undefined} [orgId]
 * @param {boolean} [enabled=true]
 * @returns {void}
 */
export function useActiveInboxChat(chatId, orgId = null, enabled = true) {
  const { setActiveInboxChat } = useUserRealtime();

  useEffect(() => {
    if (!enabled) return undefined;
    setActiveInboxChat({ chatId, orgId });
    return () => {
      setActiveInboxChat({ chatId: null, orgId: null });
    };
  }, [chatId, orgId, setActiveInboxChat, enabled]);
}
